/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useLayoutEffect, useRef } from "react";

const API_URL = import.meta.env.VITE_API_URL;
const PUBSUB_WS_URL = import.meta.env.VITE_PUBSUB_URL.replace("https://", "wss://");

// Reconexão com backoff exponencial + jitter (mesmo padrão do use-sub do app
// mobile): WS que cai volta sozinho, sem thundering herd.
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15000;

// O pubsub exige um subscribe-token curto assinado (?token=) no upgrade do WS —
// browser não manda header Authorization em WebSocket. O cliente troca o próprio
// JWT por um token curto no gateway (POST /pubsub-token). Cache em nível de
// módulo (TTL do token = 300s; guardamos ~250s); `inflight` deduplica paralelas.
let cachedToken: { token: string; exp: number } | null = null;
let inflight: Promise<string | null> | null = null;

async function getSubscribeToken(): Promise<string | null> {
  if (cachedToken && cachedToken.exp > Date.now()) return cachedToken.token;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const jwt = localStorage.getItem("@token");
      if (!jwt) return null;
      const res = await fetch(`${API_URL}/pubsub-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.token) {
        cachedToken = { token: data.token, exp: Date.now() + 250_000 };
        return data.token;
      }
      return null;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

interface Props<T> {
  endpoint: string;
  skip?: boolean;
  onData: (data: T) => void;
}

export const useSub = <T = Record<string, any>>({
  endpoint,
  skip,
  onData,
}: Props<T>) => {
  const memoizedFunction = useRef(onData);

  useLayoutEffect(() => {
    memoizedFunction.current = onData;
  }, [onData]);

  useEffect(() => {
    if (skip) return;

    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let backoff = INITIAL_BACKOFF_MS;
    let closedByCleanup = false;

    const connect = async () => {
      if (closedByCleanup) return;

      // token curto pro WS; se falhar, conecta sem (dev/pubsub sem P1 aceita).
      const subToken = await getSubscribeToken();
      if (closedByCleanup) return;
      const qs = subToken ? `?token=${encodeURIComponent(subToken)}` : "";

      ws = new WebSocket(`${PUBSUB_WS_URL}${endpoint}${qs}`);

      ws.onopen = () => {
        backoff = INITIAL_BACKOFF_MS;
      };

      ws.onmessage = ({ data }) => {
        try {
          const parsed = JSON.parse(data as string);
          memoizedFunction.current(parsed.data ?? parsed);
        } catch {
          /* mensagem não-JSON: ignora */
        }
      };

      ws.onerror = (error) => console.error(error);

      ws.onclose = () => {
        if (closedByCleanup) return;

        // 401 (token expirado/inválido) fecha o WS → limpa o cache pra buscar
        // um token fresco na próxima tentativa.
        cachedToken = null;

        const delay = Math.min(backoff, MAX_BACKOFF_MS) + Math.random() * 1000;
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
        reconnectTimeout = setTimeout(() => {
          void connect();
        }, delay);
      };
    };

    void connect();

    return () => {
      closedByCleanup = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      ws?.close();
    };
  }, [endpoint, skip]);
};
